import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import useBundleTranslation from 'i18n';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import IconMi from 'components/common/icon/IconMi';
import StatusPill, { StatusPillTone } from 'components/common/status-pill/StatusPill';
import { formatServerWallClock } from 'app/editor/graph-monitor/format';
import { useOpenTestSession } from 'app/concierge/hooks/useOpenTestSession';
import { bootstrapNounKey, neverAdded } from 'app/extreport/concierge/answering/bootstrap';
import { IKgModelCard } from 'app/extreport/concierge/answering/kgModelCardTypes';
import {
    RowStatus,
    deriveReviewStatus,
    deriveRowStatus,
    rowActionAdds,
    rowActionIcon,
    rowActionKey,
    rowRunning,
    testOffered,
} from './rowStatus';
import { useConciergeKgCards } from './useConciergeKgCards';

export interface ConciergeSourceModelGridProps extends FormControlProps {
    models: IKgModelCard[];
    entityId: number;
    actionUrl: string;
    kgEditorHomeUrl: string;
    biTool?: 'powerbi' | 'tableau' | 'other';
    modelCount?: number;
    translationNS?: string;
}

const NS = 'components/common/concierge-answering';

const CHIP_COLORS: Record<RowStatus, StatusPillTone> = {
    published: 'success',
    ready_to_publish: 'info',
    needs_review: 'warning',
    removed: 'neutral',
    not_added: 'neutral',
    removed_from_graph: 'error',
    source_removed: 'warning',
    source_missing: 'error',
    processing: 'neutral',
    failed: 'error',
};

type Translate = (key: string, params?: Record<string, unknown>) => string;

// A run that died leaves the row where it was, so the failure has to say so itself (MI-30426).
function RunFailureText({ card, t }: { card: IKgModelCard; t: Translate }) {
    const failure = card.last_run_failure;
    if (!failure) return null;
    return (
        <>
            <Typography variant="body2">
                {t('last_run_failed_on', {
                    date: `${formatServerWallClock(failure.finished_time, { month: 'short', day: 'numeric', year: 'numeric' })} ${formatServerWallClock(failure.finished_time, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}`,
                })}
            </Typography>
            {failure.message && (
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {t('last_run_failed_reason', { reason: failure.message })}
                </Typography>
            )}
        </>
    );
}

const monitorRunUrl = (card: IKgModelCard): string | undefined => {
    const runUid = card.last_run_failure?.run_uid?.trim();
    return runUid ? `/editor/graph/monitor/${runUid}` : undefined;
};

// A failure without a run uid stays readable, just not clickable.
const runLinkProps = (href: string | undefined, fallback: 'div' | 'span') =>
    href ? { component: 'a' as const, href, target: '_blank', rel: 'noopener noreferrer' } : { component: fallback };

// The KG editor opens with this report as its lens (GraphTabContent reads ?report=).
function withReportParam(url: string, elementId: number): string {
    const [path, hash] = url.split('#', 2);
    const joined = `${path}${path.includes('?') ? '&' : '?'}report=${elementId}`;
    return undefined === hash ? joined : `${joined}#${hash}`;
}

export default function ConciergeSourceModelGrid({
    controlProps,
}: FormElementControlPropsType<ConciergeSourceModelGridProps>) {
    const { t } = useBundleTranslation([controlProps.translationNS ?? NS]);
    const openTestSession = useOpenTestSession();
    const { cards, addAll, addModel, adding, addingIdentityId } = useConciergeKgCards({
        entityId: controlProps.entityId,
        actionUrl: controlProps.actionUrl,
        initial: controlProps.models ?? [],
        modelCount: controlProps.modelCount,
    });

    const biTool = cards[0]?.bi_tool ?? controlProps.biTool;
    const nounA = t(bootstrapNounKey(biTool, controlProps.modelCount ?? cards.length));
    const neverAddedNoun = t(bootstrapNounKey(biTool, 0));

    if (cards.length === 0) {
        return (
            <Box
                data-test="concierge_source_models_empty"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    px: 2,
                    py: 2,
                }}
            >
                <Typography variant="body2">{t('empty_never_added', { nounA })}</Typography>
            </Box>
        );
    }

    if (neverAdded(cards)) {
        const discovering = adding || cards.some((card) => card.registration_status === 'profiling');
        const failed = discovering ? undefined : cards.find((card) => card.last_run_failure);
        return (
            <Stack spacing={2}>
                <Box
                    data-test="concierge_source_models_empty"
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2.25,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 0.5,
                        px: 2,
                        py: 2,
                    }}
                >
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 260 }}>
                        {discovering ? t('bootstrap_discovering') : t('empty_never_added', { nounA: neverAddedNoun })}
                    </Typography>
                    <Button
                        variant="contained"
                        data-test="concierge_add_to_kg"
                        disabled={discovering}
                        startIcon={discovering ? <CircularProgress size={14} /> : <IconMi icon="new" fontSize="16" />}
                        onClick={addAll}
                    >
                        {t(discovering ? 'action_processing' : 'action_add_to_kg')}
                    </Button>
                </Box>
                {failed && (
                    <Box
                        {...runLinkProps(monitorRunUrl(failed), 'div')}
                        data-test="concierge_last_run_failure"
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            border: 1,
                            borderColor: 'warning.main',
                            borderRadius: 0.5,
                            px: 2,
                            py: 1.5,
                            color: 'text.primary',
                            textDecoration: 'none',
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08) },
                        }}
                    >
                        <Box sx={{ display: 'inline-flex', color: 'warning.main', pt: 0.25 }}>
                            <IconMi icon="alert" fontSize="16" />
                        </Box>
                        <Box>
                            <RunFailureText card={failed} t={t} />
                        </Box>
                    </Box>
                )}
            </Stack>
        );
    }

    const tool = biTool === 'tableau' ? 'Tableau' : 'Power BI';
    const openKg = (card: IKgModelCard) =>
        window.open(
            card.knowledge_graph_url
                ? withReportParam(card.knowledge_graph_url, controlProps.entityId)
                : controlProps.kgEditorHomeUrl,
            '_blank',
            'noopener',
        );
    const testTooltip = t('tooltip_test_column');

    return (
        <Table size="small" data-test="concierge_source_model_grid">
            <TableHead
                sx={(theme) => ({
                    '& .MuiTableCell-head': {
                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                        color: 'text.secondary',
                        fontSize: 12,
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                    },
                })}
            >
                <TableRow>
                    <TableCell>{t(biTool === 'tableau' ? 'col_model_tableau' : 'col_model_powerbi')}</TableCell>
                    <TableCell sx={{ width: 180 }}>{t('col_status')}</TableCell>
                    <TableCell sx={{ width: 170 }} aria-label={t('col_action_aria')} />
                    <TableCell sx={{ width: 70 }} align="center">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            {t('col_test')}
                            <Tooltip title={testTooltip}>
                                <Box
                                    component="span"
                                    tabIndex={0}
                                    data-test="concierge_test_column_info"
                                    aria-label={testTooltip}
                                    sx={{ display: 'inline-flex', color: 'text.secondary' }}
                                >
                                    <IconMi icon="info-circle" fontSize="16" />
                                </Box>
                            </Tooltip>
                        </Box>
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {cards.map((card) => {
                    const status = deriveRowStatus(card);
                    const actionKey = rowActionKey(status);
                    const testLabel = t('btn_test_title', { name: card.name });
                    return (
                        <TableRow key={card.identity_id} data-test="concierge_source_model_row" data-status={status}>
                            <TableCell>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                    }}
                                >
                                    {card.name}
                                    {status !== 'processing' && card.last_run_failure && (
                                        <Tooltip
                                            title={<RunFailureText card={card} t={t} />}
                                            slotProps={{
                                                tooltip: {
                                                    sx: { maxWidth: 440, maxHeight: '50vh', overflowY: 'auto' },
                                                },
                                            }}
                                        >
                                            <Box
                                                {...runLinkProps(monitorRunUrl(card), 'span')}
                                                tabIndex={0}
                                                data-test="concierge_row_run_failure"
                                                aria-label={t('last_run_failed_aria')}
                                                sx={{ display: 'inline-flex', color: 'warning.main', flexShrink: 0 }}
                                            >
                                                <IconMi icon="alert" fontSize="16" />
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                    {status === 'processing' ? (
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={14} />
                                            {t('status_processing')}
                                        </Box>
                                    ) : (
                                        <StatusPill
                                            tone={CHIP_COLORS[status]}
                                            dataTest="concierge_row_status"
                                            title={
                                                status === 'source_removed'
                                                    ? t('tooltip_source_removed', { tool })
                                                    : undefined
                                            }
                                        >
                                            {status === 'needs_review' && card.open_item_count > 0
                                                ? t('status_needs_review_count', { items: card.open_item_count })
                                                : t(`status_${status}`, { tool })}
                                        </StatusPill>
                                    )}
                                    {rowRunning(card, status) && (
                                        <CircularProgress
                                            size={14}
                                            data-test="concierge_row_running"
                                            aria-label={t('status_running_aria')}
                                        />
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {status !== 'source_removed' && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color={status === 'ready_to_publish' ? 'primary' : 'neutral'}
                                        sx={
                                            status === 'ready_to_publish'
                                                ? (theme) => ({
                                                      whiteSpace: 'nowrap',
                                                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                                                  })
                                                : { whiteSpace: 'nowrap' }
                                        }
                                        disabled={adding && rowActionAdds(status)}
                                        startIcon={
                                            addingIdentityId === card.identity_id ? (
                                                <CircularProgress size={14} />
                                            ) : (
                                                <IconMi icon={rowActionIcon(status)} fontSize="16" />
                                            )
                                        }
                                        data-test="concierge_row_action"
                                        onClick={() => (rowActionAdds(status) ? addModel(card) : openKg(card))}
                                    >
                                        {t(actionKey)}
                                    </Button>
                                )}
                            </TableCell>
                            <TableCell align="center">
                                {testOffered(deriveReviewStatus(card)) && (
                                    <IconButton
                                        size="small"
                                        title={testLabel}
                                        aria-label={testLabel}
                                        data-test="concierge_row_test"
                                        onClick={() =>
                                            openTestSession({
                                                modelObjectUid: card.test_session.model_object_uid,
                                                elementId: card.test_session.element_id,
                                            })
                                        }
                                    >
                                        <IconMi icon="play" fontSize="16" />
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

export class ConciergeSourceModelGridBuilder extends FormComponentBuilder {
    prepareProps(): ConciergeSourceModelGridProps {
        const p = this.elementProps.componentProps;
        return {
            ...this.controlProps,
            models: p?.models ?? [],
            entityId: p?.entityId,
            actionUrl: p?.actionUrl,
            kgEditorHomeUrl: p?.kgEditorHomeUrl,
            biTool: p?.biTool,
            modelCount: p?.modelCount,
            translationNS: p?.translationNS,
        };
    }
}
